import { PaybackPeriodOptions } from '../stores/FundingCalculator/types/fundingCalculator';
import UnderwritingIcon from '@assets/icons/underwriting.svg';
import BenchmarksIcon from '@assets/icons/tour.svg';
import ROASIcon from '@assets/icons/analytics.svg';
import ReportIcon from '@assets/icons/report-form.svg';
import FundingEligibilityIcon from '@assets/icons/invoice.svg';
import { Module } from '@stores/Modules/types/modules';
import GettingStartedIcon from '@assets/icons/getting-started-icon.svg';
import { Decimal } from 'decimal.js';
import { ChecklistType } from '@api/types/due-diligence';

const USER_ROLES = {
  VIEWER: 'Viewer',
  ADMIN: 'Admin',
};

enum DASHBOARD_ROLES {
  VIEWER = 'Viewer',
  FULL_ACCESS = 'Full Access',
  ADMIN = 'Admin',
}

enum USER_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  INVITED = 'INVITED',
  PENDING = 'PENDING',
  PENDING_OWNER_APPROVAL = 'PENDING_OWNER_APPROVAL',
  USER_PENDING_WORKSPACE_SETUP = 'USER_PENDING_WORKSPACE_SETUP',
  REJECTED = 'REJECTED',
}

const USER_STATUS_MAP: Record<USER_STATUS, string> = {
  [USER_STATUS.ACTIVE]: 'Active',
  [USER_STATUS.INACTIVE]: 'In-Active',
  [USER_STATUS.INVITED]: 'Invited',
  [USER_STATUS.PENDING]: 'Failed',
  [USER_STATUS.PENDING_OWNER_APPROVAL]: 'Pending Owner Approval',
  [USER_STATUS.USER_PENDING_WORKSPACE_SETUP]: 'Pending Workspace Setup',
  [USER_STATUS.REJECTED]: 'Rejected',
};

const ICON_COLORS = ['#5300A7', '#00866E', '#728600', '#006686', '#00866E', '#861800', '#002686'];

const STATUS_COLORS = {
  succes: {
    color: '#162936',
    background: 'transparent',
  },
  error: {
    color: '#162936',
    background: '#FFD2CC',
  },
  warning: {
    color: '#162936',
    background: '#FFE0B2',
  },
  default: {
    color: '#162936',
    background: '#9AD6D1',
  },
};

const CURRENCY_LIMIT_10K = 10000;
const CURRENCY_LIMIT_100K = 100000;
const CURRENCY_LIMIT_10M = 10000000;
const CURRENCY_LIMIT_100M = 100000000;

const DEFAULT_MARKETING_SPENDS = 250000;
const MULTIPLICATION_FACTOR_DEFAULT_MONTHLY_REVENUE = 2;
const MULTIPLICATION_FACTOR_DEFAULT_OPERATING_EXPENSES = 0.5;
const MULTIPLICATION_FACTOR_DEFAULT_PREVIOUS_MONTH_BALANCE = 1.5;

const DEFAULT_PAYBACK_PERIOD = 12;
const DEFAULT_PVX_INTEREST = 2;
const DEFAULT_PVX_FIXED_RATE_INTEREST = 1.9;
const DEFAULT_PVX_FINANCING = 0.8;
const DEFAULT_REVENUE_CHURN = 0;
const DEFAULT_UA_INCREASE = 0.025;

const ROAS_PAYBACK_PERIOD_VALUE = [
  { value: 1, label: '1' },
  { value: 3, label: '3' },
  { value: 6, label: '6' },
  { value: 9, label: '9' },
  { value: 12, label: '12' },
  { value: 15, label: '15' },
  { value: 18, label: '18' },
  { value: 24, label: '24' },
  { value: 36, label: '36' },
];

const PAYBACK_PERIOD_OPTIONS = [
  { value: 12, label: '12' },
  { value: 18, label: '18' },
  { value: 24, label: '24' },
  { value: 36, label: '36' },
];

const PVX_FINANCING_OPTIONS = [
  { value: 20, label: '20' },
  { value: 30, label: '30' },
  { value: 40, label: '40' },
  { value: 50, label: '50' },
  { value: 60, label: '60' },
  { value: 70, label: '70' },
  { value: 80, label: '80' },
];

const REVENUE_CHURN_OPTIONS = [
  { value: 0, label: '0' },
  { value: 5, label: '-5' },
  { value: 10, label: '-10' },
  { value: 15, label: '-15' },
  { value: 20, label: '-20' },
  { value: 25, label: '-25' },
  { value: 30, label: '-30' },
];

const UA_INCREASE_OPTIONS = [
  { value: 0, label: '0' },
  { value: 2.5, label: '' },
  { value: 5, label: '5' },
  { value: 7.5, label: '' },
  { value: 10, label: '10' },
  { value: 12.5, label: '' },
  { value: 15, label: '15' },
];

const DEFAULT_PAYBACK_PERIOD_OPTIONS: PaybackPeriodOptions = {
  12: [
    { month: 1, value: 20 },
    { month: 3, value: 75 },
    { month: 6, value: 100 },
    { month: 9, value: 120 },
    { month: 12, value: 135 },
  ],
  18: [
    { month: 1, value: 20 },
    { month: 3, value: 40 },
    { month: 6, value: 60 },
    { month: 9, value: 80 },
    { month: 12, value: 100 },
    { month: 15, value: 120 },
    { month: 18, value: 140 },
  ],
  24: [
    { month: 1, value: 10 },
    { month: 3, value: 30 },
    { month: 6, value: 50 },
    { month: 9, value: 70 },
    { month: 12, value: 90 },
    { month: 15, value: 105 },
    { month: 18, value: 120 },
    { month: 24, value: 140 },
  ],
  36: [
    { month: 1, value: 5 },
    { month: 3, value: 15 },
    { month: 6, value: 25 },
    { month: 9, value: 35 },
    { month: 12, value: 45 },
    { month: 15, value: 65 },
    { month: 18, value: 85 },
    { month: 24, value: 110 },
    { month: 36, value: 140 },
  ],
};

const DEFAULT_LATEST_MONTH_FINANCIALS = {
  netRevenue: 0,
  marketingSpends: 0,
  latestMonthCashBalance: 0,
  operatingExpenses: 0,
  profitBeforeTaxes: 0,
};

const CHART_TAGS = {
  CUMULATIVE_ROAS: 'Cumulative ROAS',
  MONTHLY_MARKETING_SPENDS_SURPLUS_EXPENDITURE: 'Monthly Marketing Spends - surplus expenditure',
  MONTHLY_MARKETING_SPENDS_BALANCE_DELTA: 'Monthly Marketing Spends - balance delta',
  PROFIT_BEFORE_TAXES: 'Profit Before Taxes',
  PROFIT_BEFORE_TAXES_CUMULATIVE: 'Profit Before Taxes Cumulative',
  INTEREST_PAID_BAR: 'Interest Paid - BAR',
  INTEREST_PAID_PIE: 'Interest Paid - PIE',
  REVENUE: 'Revenue',
};

const fundingPerks = [
  'Receive up to 80% of your marketing budget in cash every month',
  'Funding is collateralised by only the cohorts within the PvX funding period',
  'Pay a monthly revenue share proportionate to the amount of funding received, capped at 100% + Cash Cost',
  'Request up to a $100M facility to draw down upon as you scale UA spends',
];

const WEBSITE_LINK_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
const APP_STORE_LINK_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^https:\/\/apps\.apple\.com\/[a-z]{2}\/app\/[a-zA-Z0-9\-]+\/id\d{9,}$/;
const APP_STORE_LINK_REGEX_2 = /^https:\/\/apps\.apple\.com\/app\/id\/\d{9,}$/;
const PLAY_STORE_LINK_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^https:\/\/play\.google\.com\/store\/apps\/details\?id=[a-zA-Z0-9\._]+(&[a-zA-Z0-9=_\-]+)*$/;
const STEAM_STORE_LINK_REGEX = /^https:\/\/store\.steampowered\.com\/app\/\d+\/[a-zA-Z0-9_]+\/?$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const LINKEDIN_COMPANY_ABOUT_REGEX =
  /^https:\/\/(www\.)?linkedin\.com\/[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+(\/about)?\/?$/;
const PLAY_STORE_APP_ID_REGEX = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+[0-9a-z_]$/i;
const APP_STORE_APP_ID_REGEX = /^id[0-9]+/;

const APPLICATION_STORES = {
  APP_STORE: 'APP_STORE',
  PLAY_STORE: 'PLAY_STORE',
  WEBSITE: 'WEBSITE',
  DIVERSIFIED: 'DIVERSIFIED',
} as const;

const ATTRIBUTED_CHANNEL_OPTIONS = [
  {
    value: 'PvX Team',
    label: 'PvX Team',
  },
  {
    value: 'Conference',
    label: 'Conference',
  },
  {
    value: 'Investor',
    label: 'Investor',
  },
  {
    value: 'Founder',
    label: 'Founder',
  },
  {
    value: 'Advertisement',
    label: 'Advertisement',
  },
  {
    value: 'Other',
    label: 'Other',
  },
];

const MONTHLY_MARKETTING_SPENDS_OPTIONS = [
  {
    value: 'Under $100,000',
    label: 'Under $100,000',
  },
  {
    value: '$100,000 - $250,000',
    label: '$100,000 - $250,000',
  },
  {
    value: '$250,000 - $500,000',
    label: '$250,000 - $500,000',
  },
  {
    value: '$500,000 - $1,000,000',
    label: '$500,000 - $1,000,000',
  },
  {
    value: '> $1,000,000',
    label: '> $1,000,000',
  },
];

enum SubmoduleCategory {
  NATIVE = 'NATIVE',
  SUPERSET = 'SUPERSET',
  BLOG = 'BLOG',
  PERMISSION = 'PERMISSION',
}

const GETTING_STARTED_MODULES: Module[] = [
  {
    id: 'underwriting-report-001',
    name: 'Cohort Analysis',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'underwriting-report-sub-001',
        name: 'Cohort Analysis',
        url: 'underwriting',
        embedId: '',
        submoduleType: 'UNDERWRITTING',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${UnderwritingIcon}" }`,
  },
  {
    id: 'benchmarks-001',
    name: 'Benchmarks',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'benchmarks-sub-001',
        name: 'Benchmarks',
        url: 'benchmarks',
        embedId: '',
        submoduleType: 'BENCHMARKS',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${BenchmarksIcon}" }`,
  },
  {
    id: 'funding-eligibility-001',
    name: 'Funding Eligibility',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'funding-eligibility-sub-001',
        name: 'Funding Eligibility',
        url: 'funding-eligibility',
        embedId: '',
        submoduleType: 'FUNDING_ELIGIBILITY',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${FundingEligibilityIcon}" }`,
  },
  {
    id: 'financial-projections-001',
    name: 'Financial Projections',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'financial-projections-sub-001',
        name: 'Financial Projections',
        url: 'financial-projections',
        embedId: '',
        submoduleType: 'FINANCIAL_PROJECTIONS',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${ROASIcon}" }`,
  },
  {
    id: 'pvx-report-001',
    name: 'PvX Report',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'pvx-report-sub-001',
        name: 'PvX Report',
        url: 'pvx-report',
        embedId: '',
        submoduleType: 'INVESTOR_REPORT',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${ReportIcon}" }`,
  },
];

const GETTING_STARTED_DEMO_MODULES: Module[] = [
  {
    id: 'getting-started-demo-module',
    name: 'Getting Started',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'getting-started-demo-sub-module',
        name: 'Getting Started',
        url: 'demo',
        embedId: '',
        submoduleType: 'GETTING_STARTED_DEMO',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: false,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${GettingStartedIcon}" }`,
  },
  {
    id: 'underwriting-report-001',
    name: 'Cohort Analysis',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'underwriting-report-sub-001',
        name: 'Cohort Analysis',
        url: 'underwriting',
        embedId: '',
        submoduleType: 'UNDERWRITTING',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${UnderwritingIcon}" }`,
  },
  {
    id: 'benchmarks-001',
    name: 'Benchmarks',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'benchmarks-sub-001',
        name: 'Benchmarks',
        url: 'benchmarks',
        embedId: '',
        submoduleType: 'BENCHMARKS',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${BenchmarksIcon}" }`,
  },
  {
    id: 'funding-eligibility-001',
    name: 'Funding Eligibility',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'funding-eligibility-sub-001',
        name: 'Funding Eligibility',
        url: 'funding-eligibility',
        embedId: '',
        submoduleType: 'FUNDING_ELIGIBILITY',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${FundingEligibilityIcon}" }`,
  },
  {
    id: 'financial-projections-001',
    name: 'Financial Projections',
    requestStatus: 'APPROVED',
    submodules: [
      {
        id: 'financial-projections-sub-001',
        name: 'Financial Projections',
        url: 'financial-projections',
        embedId: '',
        submoduleType: 'FINANCIAL_PROJECTIONS_DEMO',
        defaultFilter: '',
        nativeFilterId: '',
        isPreviewEnabled: true,
        submoduleCategory: SubmoduleCategory.NATIVE,
      },
    ],
    metadata: `{ "icon": "${ROASIcon}" }`,
  },
];

const DEMO_USER_EMAILS = ['demo@pvxpartners.com'];

const EXTERNAL_SUBMODULE_TYPES = ['INVESTOR_REPORT_EXT'];

const SCHEDULE_CALL_LINK = 'https://calendar.app.google/jLF38UtBnm9MeGV38';

const PIPEDRIVE_LINK = 'https://pvxpartners.pipedrive.com/scheduler/92o4OGcv/meeting';

const PLAYSTORE_BASE_URL = 'https://play.google.com/store/apps/details?id=';
const APPSTORE_BASE_URL = 'https://apps.apple.com/app/id';

const PLAY_STORE_DOMAIN = 'play.google.com';
const APP_STORE_DOMAIN = 'apps.apple.com';

const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  DEFAULT: 'default',
} as const;

const enum SUBMODULE_VIEW {
  TAB = 'TAB',
}

const REQUESTED_SUBMODULES_META: Record<
  string,
  {
    title: string;
    subTitle: string;
    body: string;
    images: string[];
  }
> = {
  FINANCIAL_PROJECTIONS: {
    title: 'Access ROAS Projections,',
    subTitle: 'for smarter financial planning',
    body: 'Use our projections model trained on your data and fine-tuned with category insights to forecast cohort-level ROAS, helping you plan your cash flow',
    images: [
      'demo-dashboards/financial_projections_1.png',
      'demo-dashboards/financial_projections_2.png',
      'demo-dashboards/financial_projections_3.png',
    ],
  },
};

enum SelfServeDataAccessStatus {
  PENDING_USER_CREATION = 'PENDING_USER_CREATION',
  PENDING_NDA_CREATION = 'PENDING_NDA_CREATION',
  READY_TO_SEND_NDA = 'READY_TO_SEND_NDA',
  PENDING_NDA_SIGNATURE = 'PENDING_NDA_SIGNATURE',
  ACCESS_GRANTED = 'ACCESS_GRANTED',
  PENDING_ACCESS = 'PENDING_ACCESS',
  DATA_FETCH_FAILED = 'DATA_FETCH_FAILED',
  DATA_FETCH_FAILED_NO_COST_AND_REVENUE_INFORMATION = 'DATA_FETCH_FAILED_NO_COST_AND_REVENUE_INFORMATION',
  DATA_FETCH_FAILED_NO_COST_INFORMATION = 'DATA_FETCH_FAILED_NO_COST_INFORMATION',
  DATA_FETCH_FAILED_NO_REVENUE_INFORMATION = 'DATA_FETCH_FAILED_NO_REVENUE_INFORMATION',
  DATA_FETCH_FAILED_INVALID_APP_ID = 'DATA_FETCH_FAILED_INVALID_APP_ID',
  DATA_FETCH_FAILED_INVALID_ACCOUNT_NAME = 'DATA_FETCH_FAILED_INVALID_ACCOUNT_NAME',
  DASHBOARD_CREATION_FAILED = 'DASHBOARD_CREATION_FAILED',
  DATA_FETCH_SUCCESSFUL = 'DATA_FETCH_SUCCESSFUL',
  PUBLISHED = 'PUBLISHED',
  CALL_SCHEDULED = 'CALL_SCHEDULED',
  REOPEN = 'REOPENED',
  RERUN_FAILED = 'RERUN_FAILED',
  RERUN_FAILED_INVALID_ACCOUNT_NAME = 'RERUN_FAILED_INVALID_ACCOUNT_NAME',
  RERUN_FAILED_INVALID_APP_ID = 'RERUN_FAILED_INVALID_APP_ID',
  RERUN_FAILED_NO_COST_INFORMATION = 'RERUN_FAILED_NO_COST_INFORMATION',
  RERUN_FAILED_NO_REVENUE_INFORMATION = 'RERUN_FAILED_NO_REVENUE_INFORMATION',
  RERUN_FAILED_NO_COST_AND_REVENUE_INFORMATION = 'RERUN_FAILED_NO_COST_AND_REVENUE_INFORMATION',
  DELETED = 'DELETED',
}

export const SelfServeDataAccessStatusMap: Record<SelfServeDataAccessStatus, string> = {
  [SelfServeDataAccessStatus.PENDING_USER_CREATION]: 'Pending User Creation',
  [SelfServeDataAccessStatus.PENDING_NDA_CREATION]: 'Pending NDA Creation',
  [SelfServeDataAccessStatus.READY_TO_SEND_NDA]: 'Ready To Send NDA',
  [SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE]: 'Pending NDA Signature',
  [SelfServeDataAccessStatus.PENDING_ACCESS]: 'Pending Access',
  [SelfServeDataAccessStatus.CALL_SCHEDULED]: 'Call Scheduled',
  [SelfServeDataAccessStatus.ACCESS_GRANTED]: 'Access Granted',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED]: 'Data Fetch Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_INFORMATION]: 'Data Fetch Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_REVENUE_INFORMATION]: 'Data Fetch Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_AND_REVENUE_INFORMATION]:
    'Data Fetch Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_APP_ID]: 'Data Fetch Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_ACCOUNT_NAME]: 'Data Fetch Failed',
  [SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED]: 'Dashboard Creation Failed',
  [SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL]: 'Data Fetch Successful',
  [SelfServeDataAccessStatus.PUBLISHED]: 'Published',
  [SelfServeDataAccessStatus.REOPEN]: 'Reopened',
  [SelfServeDataAccessStatus.RERUN_FAILED]: 'Re-Run Failed',
  [SelfServeDataAccessStatus.RERUN_FAILED_INVALID_ACCOUNT_NAME]: 'Re-Run Failed',
  [SelfServeDataAccessStatus.RERUN_FAILED_INVALID_APP_ID]: 'Re-Run Failed',
  [SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_INFORMATION]: 'Re-Run Failed',
  [SelfServeDataAccessStatus.RERUN_FAILED_NO_REVENUE_INFORMATION]: 'Re-Run Failed',
  [SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_AND_REVENUE_INFORMATION]: 'Re-Run Failed',
  [SelfServeDataAccessStatus.DELETED]: 'Deleted',
};

enum ERROR_TYPE_FILTER_VALUES {
  FETCH_FAILED_GENERAL = 'FETCH_FAILED_GENERAL',
  INVALID_APP_ID = 'INVALID_APP_ID',
  INVALID_ACCOUNT_NAME = 'INVALID_ACCOUNT_NAME',
  NO_COST_INFORMATION = 'NO_COST_INFORMATION',
  NO_REVENUE_INFORMATION = 'NO_REVENUE_INFORMATION',
  NO_COST_AND_REVENUE_INFORMATION = 'NO_COST_AND_REVENUE_INFORMATION',
}

const ERROR_TYPE_FILTER_OPTIONS = [
  {
    label: '-',
    value: ERROR_TYPE_FILTER_VALUES.FETCH_FAILED_GENERAL,
    keyOptions: [
      SelfServeDataAccessStatus.DATA_FETCH_FAILED,
      SelfServeDataAccessStatus.RERUN_FAILED,
    ],
  },
  {
    label: 'Invalid App ID',
    value: ERROR_TYPE_FILTER_VALUES.INVALID_APP_ID,
    keyOptions: [
      SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_APP_ID,
      SelfServeDataAccessStatus.RERUN_FAILED_INVALID_APP_ID,
    ],
  },
  {
    label: 'Invalid Account Name',
    value: ERROR_TYPE_FILTER_VALUES.INVALID_ACCOUNT_NAME,
    keyOptions: [
      SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_ACCOUNT_NAME,
      SelfServeDataAccessStatus.RERUN_FAILED_INVALID_ACCOUNT_NAME,
    ],
  },
  {
    label: 'No Cost Information',
    value: ERROR_TYPE_FILTER_VALUES.NO_COST_INFORMATION,
    keyOptions: [
      SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_INFORMATION,
      SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_INFORMATION,
    ],
  },
  {
    label: 'No Revenue Information',
    value: ERROR_TYPE_FILTER_VALUES.NO_REVENUE_INFORMATION,
    keyOptions: [
      SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_REVENUE_INFORMATION,
      SelfServeDataAccessStatus.RERUN_FAILED_NO_REVENUE_INFORMATION,
    ],
  },
  {
    label: 'No Cost And Revenue Information',
    value: ERROR_TYPE_FILTER_VALUES.NO_COST_AND_REVENUE_INFORMATION,
    keyOptions: [
      SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_AND_REVENUE_INFORMATION,
      SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_AND_REVENUE_INFORMATION,
    ],
  },
];
const DATA_FETCH_ERROR_TYPE_MAP = {
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED]: '-',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_INFORMATION]: 'No Cost Information',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_REVENUE_INFORMATION]: 'No Revenue Information',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_AND_REVENUE_INFORMATION]:
    'No Cost And Revenue Information',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_APP_ID]: 'Invalid App ID',
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_ACCOUNT_NAME]: 'Invalid Account Name',
};

const RERUN_FAILED_ERROR_TYPE_MAP = {
  [SelfServeDataAccessStatus.RERUN_FAILED]: '-',
  [SelfServeDataAccessStatus.RERUN_FAILED_INVALID_APP_ID]: 'Invalid App ID',
  [SelfServeDataAccessStatus.RERUN_FAILED_INVALID_ACCOUNT_NAME]: 'Invalid Account Name',
  [SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_INFORMATION]: 'No Cost Information',
  [SelfServeDataAccessStatus.RERUN_FAILED_NO_REVENUE_INFORMATION]: 'No Revenue Information',
  [SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_AND_REVENUE_INFORMATION]:
    'No Cost And Revenue Information',
};
enum MODES {
  EDIT = 'EDIT',
  CREATE = 'CREATE',
  LISTING = 'LISTING',
}

export const SELF_SERVE_PENDING_USER_CREATION_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PENDING_USER_CREATION,
]);
export const SELF_SERVE_PENDING_NDA_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PENDING_NDA_CREATION,
  SelfServeDataAccessStatus.READY_TO_SEND_NDA,
  SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE,
]);
export const SELF_SERVE_PENDING_ACCESS_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PENDING_ACCESS,
  SelfServeDataAccessStatus.CALL_SCHEDULED,
]);
export const SELF_SERVE_PROCESSING_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.ACCESS_GRANTED,
  SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL,
]);
export const SELF_SERVE_PROCESSED_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.PUBLISHED,
]);
export const SELF_SERVE_DATA_FETCH_FAILED_STATUSES = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_INFORMATION,
  SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_REVENUE_INFORMATION,
  SelfServeDataAccessStatus.DATA_FETCH_FAILED_NO_COST_AND_REVENUE_INFORMATION,
  SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_APP_ID,
  SelfServeDataAccessStatus.DATA_FETCH_FAILED_INVALID_ACCOUNT_NAME,
]);
export const SELF_SERVE_FAILED_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.DATA_FETCH_FAILED,
  SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED,
]);
export const SELF_SERVE_RERUN_FAILED_STATUSES = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.RERUN_FAILED_INVALID_ACCOUNT_NAME,
  SelfServeDataAccessStatus.RERUN_FAILED_INVALID_APP_ID,
  SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_INFORMATION,
  SelfServeDataAccessStatus.RERUN_FAILED_NO_REVENUE_INFORMATION,
  SelfServeDataAccessStatus.RERUN_FAILED_NO_COST_AND_REVENUE_INFORMATION,
]);
export const SELF_SERVE_POST_PROCESS_STATUS = new Set<SelfServeDataAccessStatus>([
  SelfServeDataAccessStatus.REOPEN,
  SelfServeDataAccessStatus.RERUN_FAILED,
  ...SELF_SERVE_RERUN_FAILED_STATUSES,
]);

export enum DataAccessProvider {
  APPS_FLYER = 'APPS_FLYER',
  ADJUST = 'ADJUST',
  BIGQUERY = 'BIGQUERY',
  GCS = 'GCS',
  S3 = 'S3',
  MYSQL = 'MYSQL',
  SNOWFLAKE = 'SNOWFLAKE',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  AGGREGATED_DATA = 'AGGREGATED_DATA',
  DIVERSIFIED = 'DIVERSIFIED',
  NONE = 'NONE',
}

export const MMP_SOURCE_MAP: Record<DataAccessProvider, string> = {
  [DataAccessProvider.APPS_FLYER]: 'AppsFlyer',
  [DataAccessProvider.ADJUST]: 'Adjust',
  [DataAccessProvider.NONE]: 'None',
  [DataAccessProvider.BIGQUERY]: 'BigQuery',
  [DataAccessProvider.GCS]: 'Google Cloud Storage',
  [DataAccessProvider.S3]: 'Amazon S3',
  [DataAccessProvider.MYSQL]: 'MySQL',
  [DataAccessProvider.SNOWFLAKE]: 'Snowflake',
  [DataAccessProvider.GOOGLE_DRIVE]: 'Cloud Storage',
  [DataAccessProvider.AGGREGATED_DATA]: 'Aggregated Cohort Data',
  [DataAccessProvider.DIVERSIFIED]: 'Diversified',
};

enum GameStatus {
  FUNDED = 'FUNDED',
  NOT_FUNDED = 'NOT_FUNDED',
  PAUSED_FUNDING = 'PAUSED_FUNDING',
}

enum InvestorReportShareAccessType {
  INVESTOR = 'INVESTOR',
  INDIVIDUAL = 'INDIVIDUAL',
}

enum InvestorActivityType {
  VIEWED_REPORT = 'VIEWED',
  VIEWED_CONTACT_DETAILS = 'VIEWED_CONTACT_DETAILS',
}

const InvestorActivityLabelMap: Record<InvestorActivityType, string> = {
  [InvestorActivityType.VIEWED_REPORT]: 'Viewed Report',
  [InvestorActivityType.VIEWED_CONTACT_DETAILS]: 'Viewed Contact Details',
};

export const GAME_STATUS_MAP: Record<GameStatus, string> = {
  [GameStatus.FUNDED]: 'Funded',
  [GameStatus.NOT_FUNDED]: 'Not Funded',
  [GameStatus.PAUSED_FUNDING]: 'Paused Funding',
};

enum PricingMethodology {
  FIXED_RATE = 'FIXED_RATE',
  FIXED_RATE_EXPOSURE_RATCHET = 'FIXED_RATE_EXPOSURE_RATCHET',
  FIXED_RATE_FUNDING_RATCHET = 'FIXED_RATE_FUNDING_RATCHET',
  FIXED_RATE_ACCRUED = 'FIXED_RATE_ACCRUED',
  FIXED_RATE_ACCRUED_EXPOSURE_RATCHET = 'FIXED_RATE_ACCRUED_EXPOSURE_RATCHET',
  FIXED_RATE_ACCRUED_FUNDING_RATCHET = 'FIXED_RATE_ACCRUED_FUNDING_RATCHET',
  SIMPLE_INTEREST = 'SIMPLE_INTEREST',
  DYNAMIC_RETURN_CAP = 'DYNAMIC_RETURN_CAP',
  DYNAMIC_RETURN_CAP_V2 = 'DYNAMIC_RETURN_CAP_V2',
  PVX_EARLY = 'PVX_EARLY',
  CAPPED_SIMPLE_INTEREST = 'CAPPED_SIMPLE_INTEREST',
  SIMPLE_INTEREST_RATCHET = 'SIMPLE_INTEREST_RATCHET',
  CUSTOM = 'CUSTOM',
  CUSTOM_PLAYSOME = 'CUSTOM_PLAYSOME',
  CUSTOM_FLIFF = 'CUSTOM_FLIFF',
  SIMPLE_INTEREST_EXPOSURE_RATCHET = 'SIMPLE_INTEREST_EXPOSURE_RATCHET',
  SIMPLE_INTEREST_FUNDING_RATCHET = 'SIMPLE_INTEREST_FUNDING_RATCHET',
  DYNAMIC_RETURN_CAP_EXPOSURE_RATCHET = 'DYNAMIC_RETURN_CAP_EXPOSURE_RATCHET',
  DYNAMIC_RETURN_CAP_FUNDING_RATCHET = 'DYNAMIC_RETURN_CAP_FUNDING_RATCHET',
}

const RATCHET_PRICING_METHODOLOGIES = [
  PricingMethodology.SIMPLE_INTEREST_RATCHET,
  PricingMethodology.SIMPLE_INTEREST_EXPOSURE_RATCHET,
  PricingMethodology.SIMPLE_INTEREST_FUNDING_RATCHET,
  PricingMethodology.DYNAMIC_RETURN_CAP_EXPOSURE_RATCHET,
  PricingMethodology.DYNAMIC_RETURN_CAP_FUNDING_RATCHET,
  PricingMethodology.FIXED_RATE_EXPOSURE_RATCHET,
  PricingMethodology.FIXED_RATE_FUNDING_RATCHET,
  PricingMethodology.FIXED_RATE_ACCRUED_EXPOSURE_RATCHET,
  PricingMethodology.FIXED_RATE_ACCRUED_FUNDING_RATCHET,
];

const PRICING_METHODOLOGY_MAP: Record<PricingMethodology, string> = {
  [PricingMethodology.FIXED_RATE_ACCRUED]: 'Accrued Fixed Rate',
  [PricingMethodology.FIXED_RATE_ACCRUED_EXPOSURE_RATCHET]:
    'Accrued Fixed Rate Ratchet (Exposure Based)',
  [PricingMethodology.FIXED_RATE_ACCRUED_FUNDING_RATCHET]:
    'Accrued Fixed Rate Ratchet (Funding Amount Based)',
  [PricingMethodology.FIXED_RATE]: 'Fixed Rate',
  [PricingMethodology.FIXED_RATE_EXPOSURE_RATCHET]: 'Fixed Rate Ratchet (Exposure Based)',
  [PricingMethodology.FIXED_RATE_FUNDING_RATCHET]: 'Fixed Rate Ratchet (Funding Amount Based)',
  [PricingMethodology.SIMPLE_INTEREST]: 'Simple Interest',
  [PricingMethodology.DYNAMIC_RETURN_CAP]: 'Dynamic Return Cap',
  [PricingMethodology.DYNAMIC_RETURN_CAP_V2]: 'Dynamic Return Cap V2',
  [PricingMethodology.PVX_EARLY]: 'PVX Early',
  [PricingMethodology.CAPPED_SIMPLE_INTEREST]: 'Capped Simple Interest',
  [PricingMethodology.SIMPLE_INTEREST_RATCHET]: 'Simple Interest Ratchet',
  [PricingMethodology.SIMPLE_INTEREST_EXPOSURE_RATCHET]: 'Simple Interest Ratchet (Exposure Based)',
  [PricingMethodology.SIMPLE_INTEREST_FUNDING_RATCHET]:
    'Simple Interest Ratchet (Funding Amount Based)',
  [PricingMethodology.DYNAMIC_RETURN_CAP_EXPOSURE_RATCHET]:
    'Dynamic Return Cap V2 Ratchet (Exposure Based)',
  [PricingMethodology.DYNAMIC_RETURN_CAP_FUNDING_RATCHET]:
    'Dynamic Return Cap V2 Ratchet (Funding Amount Based)',
  [PricingMethodology.CUSTOM]: 'Custom',
  [PricingMethodology.CUSTOM_PLAYSOME]: 'Custom PlaySome',
  [PricingMethodology.CUSTOM_FLIFF]: 'Custom Fliff',
};

enum CohortStatus {
  ONGOING = 'ONGOING',
  CLOSED = 'CLOSED',
  DEFAULTED = 'DEFAULTED',
  IMPAIRED = 'IMPAIRED',
}

const COHORT_STATUS_MAP: Record<CohortStatus, string> = {
  [CohortStatus.ONGOING]: 'Ongoing',
  [CohortStatus.CLOSED]: 'Closed',
  [CohortStatus.DEFAULTED]: 'Defaulted',
  [CohortStatus.IMPAIRED]: 'Impaired',
};

enum DealStatus {
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  NONE = 'NONE',
  INACTIVE = 'INACTIVE',
}

const DEAL_STATUS_MAP: Record<DealStatus, string> = {
  [DealStatus.ACTIVE]: 'Active',
  [DealStatus.PENDING]: 'Pending',
  [DealStatus.NONE]: 'None',
  [DealStatus.INACTIVE]: 'Inactive',
};

const SEARCH_DEBOUNCE_TIME = 800;

enum PayoutStatus {
  PENDING = 'PENDING',
  INITIATED = 'INITIATED',
  PAID = 'PAID',
}

const PAYOUT_STATUS_MAP: Record<PayoutStatus, string> = {
  [PayoutStatus.PENDING]: 'Pending',
  [PayoutStatus.INITIATED]: 'Initiated',
  [PayoutStatus.PAID]: 'Paid',
};

enum CommissionType {
  PERPETUAL = 'PERPETUAL',
  COHORT_BASED = 'COHORT_BASED',
}

export enum ReferrerType {
  REFERRER = 'REFERRER',
  SALES = 'SALES',
}

export enum PayoutPaymentType {
  FINAL = 'FINAL',
  ADVANCE = 'ADVANCE',
}

const COMMISSION_TYPE_MAP: Record<CommissionType, string> = {
  [CommissionType.PERPETUAL]: 'Perpetual',
  [CommissionType.COHORT_BASED]: 'Cohort Based',
};

enum TransactionType {
  COMMISSION = 'COMMISSION',
}

const TRANSACTION_TYPE_MAP: Record<TransactionType, string> = {
  [TransactionType.COMMISSION]: 'Commission',
};

const DEFAULT_PAGE_NUMBER = 0;
const DEFAULT_PAGE_SIZE = 25;

const VIRTUALIZER_OVERSCAN = 5;
const VIRTUALIZER_ESTIMATE_SIZE = 40;

enum ChartType {
  MULTI_LINE_CHART = 'MULTI_LINE_CHART',
  BAR_CHART = 'MULTI_BAR_CHART',
  LINE_BAR_CHART = 'LINE_BAR_CHART',
  TABLE = 'TABLE',
  STACKED_BAR_CHART = 'STACKED_BAR_CHART',
  BOX_PLOT_CHART = 'BOX_PLOT_CHART',
  BUBBLE_CHART = 'BUBBLE_CHART',
  PIVOT_TABLE = 'PIVOT_TABLE',
  RATIOS_SUMMARY_TABLE = 'RATIOS_SUMMARY_TABLE',
}

const LANGUAGES = [
  { locale: 'en', label: 'EN', displayLabel: 'EN', code: 'EN' },
  { locale: 'zh-CN', label: '普通话', displayLabel: 'CN', code: 'ZH' },
];

const LOCALE_LANGUAGE_MAPPING: Record<string, string> = {
  en: 'EN',
  'zh-CN': 'ZH',
};

const LOCAL_STORAGE_KEYS = {
  HIDE_HELP_VIDEO: 'hideHelpVideo',
  DEV_CONTROL_ENABLED: 'devControlEnabled',
};

enum ApplicationEnvironment {
  DEV = 'dev',
  PROD = 'prod',
}

const SESSION_STORAGE_KEYS = {
  ORIGIN: 'origin',
  LAMBDA_BOT_HISTORY: 'lambdaBotHistory',
  SUBMODULE_TO_REDIRECT: 'submoduleToRedirect',
  MARKET_SCANNER_CATEGORY: 'marketScannerCategory',
  MARKET_SCANNER_GENRE: 'marketScannerGenre',
};

const MONTH_COLUMN_PATTERN = /M\d+/;

enum SUBMODULE_TYPE {
  INVESTMENT_PERFORMANCE = 'INVESTMENT_PERFORMANCE',
  FUNDING_REQUEST = 'FUNDING_REQUEST',
  FUNDING_REQUEST_TRACKER = 'FUNDING_REQUEST_TRACKER',
  GAME_PERFORMANCE = 'GAME_PERFORMANCE',
  BENCHMARKS_CUSTOM = 'BENCHMARKS_CUSTOM',
  FUNDING_ELIGIBILITY = 'FUNDING_ELIGIBILITY',
  DEAL_SETUP = 'DEAL_SETUP',
  AD_IMPRESSIONS = 'AD_IMPRESSIONS',
  ACCOUNTING_REVENUE = 'ACCOUNTING_REVENUE',
  GETTING_STARTED = 'GETTING_STARTED',
  GETTING_STARTED_DEMO = 'GETTING_STARTED_DEMO',
  COMMISSIONS_DASHBOARD = 'COMMISSIONS_DASHBOARD',
  DOWNLOADABLE_REPORTS = 'DOWNLOADABLE_REPORTS',
  REFERRER_SETUP = 'REFERRER_SETUP',
  COHORT_ANALYSIS = 'COHORT_ANALYSIS',
  FINANCIAL_REPORTING = 'FINANCIAL_REPORTING',
  FINANCIAL_REPORTING_DASHBOARD = 'FINANCIAL_REPORTING_DASHBOARD',
  DEAL_SETUP_DASHBOARD = 'DEAL_SETUP_DASHBOARD',
  FINANCING_REPORT_SUMMARY = 'FINANCING_REPORT_SUMMARY',
  INVESTOR_REPORT = 'INVESTOR_REPORT',
  INVESTOR_REPORT_V2 = 'INVESTOR_REPORT_V2',
  INVESTOR_REPORT_V2_EXT = 'INVESTOR_REPORT_V2_EXT',
  SELF_SERVE = 'SELF_SERVE',
  FINANCIAL_PROJECTIONS = 'FINANCIAL_PROJECTIONS',
  FINANCIAL_PROJECTIONS_V2 = 'FINANCIAL_PROJECTIONS_V2',
  CURRENT_COHORTS = 'CURRENT_COHORTS',
  FUTURE_COHORTS = 'FUTURE_COHORTS',
  ALERTS_DASHBOARD = 'ALERTS_DASHBOARD',
  MODEL_SETTINGS = 'MODEL_SETTINGS',
  MODEL_REQUESTS = 'MODEL_REQUESTS',
  DUE_DILIGENCE = 'DUE_DILIGENCE',
  COMMISSIONS_PAYOUT = 'COMMISSIONS_PAYOUT',
  MARKET_INTELLIGENCE = 'MARKET_INTELLIGENCE',
  MARKET_INTELLIGENCE_SETTINGS = 'MARKET_INTELLIGENCE_SETTINGS',
  EXPOSURE_FORECASTING = 'EXPOSURE_FORECASTING',
  THRESHOLD_BREACH_ALERTS = 'THRESHOLD_BREACH_ALERTS',
  FUND_PERFORMANCE = 'FUND_PERFORMANCE',
  LP_SETUP = 'LP_SETUP',
  FUND_ALLOCATION = 'FUND_ALLOCATION',
  CLIENT_USER_MANAGEMENT = 'CLIENT_USER_MANAGEMENT',
  COMMISSIONS_PAYINS = 'COMMISSIONS_PAYINS',
  IMPACT_REPORT = 'IMPACT_REPORT',
  LAMBDA_INTELLIGENCE = 'LAMBDA_INTELLIGENCE',
  SPEND_IMPACT = 'SPEND_IMPACT',
  MARKET_SCAN = 'MARKET_SCAN',
}

enum SubmoduleAccessStatus {
  REQUESTABLE = 'REQUESTABLE',
  REQUESTED = 'REQUESTED',
  GRANTED = 'GRANTED',
  DELETED = 'DELETED',
  OVERDUE = 'OVERDUE',
  FAILED = 'FAILED',
  READY_TO_PUBLISH = 'READY_TO_PUBLISH',
}

const CLIENTS_SUBMODULE_TYPES = [
  SUBMODULE_TYPE.GETTING_STARTED,
  SUBMODULE_TYPE.COHORT_ANALYSIS,
  SUBMODULE_TYPE.FUNDING_ELIGIBILITY,
  SUBMODULE_TYPE.BENCHMARKS_CUSTOM,
  SUBMODULE_TYPE.INVESTOR_REPORT,
  SUBMODULE_TYPE.FINANCIAL_REPORTING,
  SUBMODULE_TYPE.THRESHOLD_BREACH_ALERTS,
  SUBMODULE_TYPE.DOWNLOADABLE_REPORTS,
  SUBMODULE_TYPE.FUNDING_REQUEST,
  SUBMODULE_TYPE.FINANCING_REPORT_SUMMARY,
  SUBMODULE_TYPE.DUE_DILIGENCE,
  SUBMODULE_TYPE.CLIENT_USER_MANAGEMENT,
  SUBMODULE_TYPE.FINANCIAL_PROJECTIONS,
  SUBMODULE_TYPE.FINANCIAL_PROJECTIONS_V2,
  SUBMODULE_TYPE.SPEND_IMPACT,
  SUBMODULE_TYPE.INVESTOR_REPORT_V2,
  SUBMODULE_TYPE.INVESTOR_REPORT_V2_EXT,
  // Add other submodule types here for userManagementModules
];

const PVX_PARTNERS_EMAIL_DOMAIN = '@pvxpartners.com';

enum InvestorSigningAuthority {
  PVX = 'PVX',
  GC = 'GC',
  HAF = 'HAF',
}

enum RunwayAnalysisStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  DECLINED = 'DECLINED',
}

enum ScenarioStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  CALCULATION_COMPLETED = 'CALCULATION_COMPLETED',
  COMPLETED = 'COMPLETED',
}

enum ScenarioOrigin {
  DEFAULT = 'DEFAULT',
  ADMIN = 'ADMIN',
  CLIENT = 'CLIENT',
}

enum RoasCurveType {
  REFERENCE = 'REFERENCE',
  THRESHOLD = 'THRESHOLD',
  PROJECTED = 'PROJECTED',
  EXTRAPOLATED_THRESHOLD = 'EXTRAPOLATED_THRESHOLD',
}

enum FundAllocationStatus {
  PENDING = 'PENDING',
  ALLOCATED = 'ALLOCATED',
  DEFAULT_ALLOCATED = 'DEFAULT_ALLOCATED',
  LP_ALLOCATED = 'LP_ALLOCATED',
}

const INVESTOR_LABEL_MAP: Record<string, string> = {
  [InvestorSigningAuthority.PVX]: 'PvX',
  [InvestorSigningAuthority.GC]: 'GC',
  [InvestorSigningAuthority.HAF]: 'HAF',
};

const FUND_ALLOCATION_STATUS_MAP: Record<FundAllocationStatus, string> = {
  [FundAllocationStatus.PENDING]: 'Pending',
  [FundAllocationStatus.ALLOCATED]: 'Allocated',
  [FundAllocationStatus.DEFAULT_ALLOCATED]: 'Allocated',
  [FundAllocationStatus.LP_ALLOCATED]: 'LP Allocated',
};

const FUND_ALLOCATION_STATUS_INDICATION_MAP: Record<FundAllocationStatus, string> = {
  [FundAllocationStatus.PENDING]: '#FFA726',
  [FundAllocationStatus.ALLOCATED]: '#51B8B0',
  [FundAllocationStatus.DEFAULT_ALLOCATED]: '#51B8B0',
  [FundAllocationStatus.LP_ALLOCATED]: '#51B8B0',
};

const USER_APPROVAL_STATUS = {
  PENDING_OWNER_APPROVAL: 'PENDING_OWNER_APPROVAL',
  COMPANY_CREATION_REQUESTED: 'COMPANY_CREATION_REQUESTED',
  USER_PENDING_WORKSPACE_SETUP: 'USER_PENDING_WORKSPACE_SETUP',
  REJECTED: 'REJECTED',
} as const;

const INVITATION_STATUS_CODE = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  EXPIRED: 'EXPIRED',
} as const;

enum FinancialStatementsStatus {
  SUBMITTED = 'SUBMITTED',
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
}

enum FinancialStatementDocumentType {
  BALANCE_SHEET = 'BALANCE_SHEET',
  CASH_FLOW = 'CASH_FLOW',
  PROFIT_AND_LOSS = 'PROFIT_AND_LOSS',
}

enum FinancialStatementType {
  ANNUAL = 'ANNUAL',
  QUARTERLY = 'QUARTERLY',
  SEMI_ANNUAL = 'SEMI_ANNUAL',
  MONTHLY = 'MONTHLY',
}

enum FinancialStatementAuditStatus {
  AUDITED = 'AUDITED',
  UNAUDITED = 'UN_AUDITED',
}

enum FinancialStatementAdHocAdjustmentType {
  PAYMENT_TO_PVX = 'PAYMENT_TO_PVX',
  PAYMENT_FROM_PVX = 'PAYMENT_FROM_PVX',
}

const EPSILON = new Decimal('1e-8');

const DD_TEMPLATE_WITH_NO_LANGUAGE_TOGGLE_SUPPORT = [
  ChecklistType.STANDARD,
  ChecklistType.REAL_MONEY_GAMING,
  ChecklistType.CHINESE_DEALS,
];

enum PvxImpactSummaryCardType {
  UA_SPEND_SCALING = 'UA_SPEND_SCALING',
  TOTAL_PVX_DEPLOYED = 'TOTAL_PVX_DEPLOYED',
  EXPECTED_CAPITAL_FREED = 'EXPECTED_CAPITAL_FREED',
  UTILISATION_RATE = 'UTILISATION_RATE',
  PVX_COST = 'PVX_COST',
  FORECAST_COMPANY_ROIC = 'FORECAST_COMPANY_ROIC',
  CASH_IMPACT_ACTUAL_USAGE = 'CASH_IMPACT_ACTUAL_USAGE',
  CASH_IMPACT_FULL_UTILISATION = 'CASH_IMPACT_FULL_UTILISATION',
  EXECUTIVE_SUMMARY = 'EXECUTIVE_SUMMARY',
}

const USD_CURRENCY_CODE = 'USD';

const SUPPORTS_CLAYRIO_ANALYSIS = [ChecklistType.UNIFIED];

enum QueryParamsKeyEnum {
  INVESTOR_REPORT_PREVIEW_MODE = 'preview',
  INVESTOR_REPORT_EXT_REDIRECT_CLIENT_CODE = 'client_code',
  INVITE_ID = 'invite_id',
  INVESTOR_REPORT_CLIENT_VIEW_INITIAL_CLIENT_CODE = 'client_code',
}

enum MarketCeilingSpendType {
  MARKET = 'MARKET',
  CLIENT = 'CLIENT',
}

const TEXT_NA = 'N/A';

export {
  USER_ROLES,
  USER_APPROVAL_STATUS,
  INVITATION_STATUS_CODE,
  DASHBOARD_ROLES,
  USER_STATUS,
  USER_STATUS_MAP,
  ICON_COLORS,
  STATUS_COLORS,
  ROAS_PAYBACK_PERIOD_VALUE,
  PAYBACK_PERIOD_OPTIONS,
  DEFAULT_PAYBACK_PERIOD,
  PVX_FINANCING_OPTIONS,
  DEFAULT_PVX_INTEREST,
  DEFAULT_PVX_FINANCING,
  DEFAULT_PVX_FIXED_RATE_INTEREST,
  DEFAULT_REVENUE_CHURN,
  REVENUE_CHURN_OPTIONS,
  DEFAULT_UA_INCREASE,
  UA_INCREASE_OPTIONS,
  DEFAULT_PAYBACK_PERIOD_OPTIONS,
  CHART_TAGS,
  DEFAULT_LATEST_MONTH_FINANCIALS,
  DEFAULT_MARKETING_SPENDS,
  fundingPerks,
  WEBSITE_LINK_REGEX,
  APP_STORE_LINK_REGEX,
  APP_STORE_LINK_REGEX_2,
  PLAY_STORE_LINK_REGEX,
  STEAM_STORE_LINK_REGEX,
  PLAY_STORE_APP_ID_REGEX,
  APP_STORE_APP_ID_REGEX,
  EMAIL_REGEX,
  LINKEDIN_COMPANY_ABOUT_REGEX,
  CURRENCY_LIMIT_10K,
  CURRENCY_LIMIT_100K,
  CURRENCY_LIMIT_10M,
  CURRENCY_LIMIT_100M,
  MULTIPLICATION_FACTOR_DEFAULT_MONTHLY_REVENUE,
  MULTIPLICATION_FACTOR_DEFAULT_OPERATING_EXPENSES,
  MULTIPLICATION_FACTOR_DEFAULT_PREVIOUS_MONTH_BALANCE,
  APPLICATION_STORES,
  ATTRIBUTED_CHANNEL_OPTIONS,
  MONTHLY_MARKETTING_SPENDS_OPTIONS,
  TOAST_TYPES,
  SCHEDULE_CALL_LINK,
  PIPEDRIVE_LINK,
  DEMO_USER_EMAILS,
  SUBMODULE_VIEW,
  REQUESTED_SUBMODULES_META,
  SelfServeDataAccessStatus,
  MODES,
  GETTING_STARTED_MODULES,
  GETTING_STARTED_DEMO_MODULES,
  GameStatus,
  PricingMethodology,
  PRICING_METHODOLOGY_MAP,
  DealStatus,
  DEAL_STATUS_MAP,
  SEARCH_DEBOUNCE_TIME,
  PayoutStatus,
  PAYOUT_STATUS_MAP,
  CommissionType,
  COMMISSION_TYPE_MAP,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  CohortStatus,
  COHORT_STATUS_MAP,
  TransactionType,
  TRANSACTION_TYPE_MAP,
  VIRTUALIZER_OVERSCAN,
  VIRTUALIZER_ESTIMATE_SIZE,
  ChartType,
  SubmoduleCategory,
  LANGUAGES,
  LOCALE_LANGUAGE_MAPPING,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  MONTH_COLUMN_PATTERN,
  ApplicationEnvironment,
  SUBMODULE_TYPE,
  SubmoduleAccessStatus,
  PVX_PARTNERS_EMAIL_DOMAIN,
  EXTERNAL_SUBMODULE_TYPES,
  InvestorReportShareAccessType,
  InvestorActivityType,
  InvestorActivityLabelMap,
  PLAYSTORE_BASE_URL,
  APPSTORE_BASE_URL,
  PLAY_STORE_DOMAIN,
  APP_STORE_DOMAIN,
  INVESTOR_LABEL_MAP,
  RunwayAnalysisStatus,
  RoasCurveType,
  FundAllocationStatus,
  FUND_ALLOCATION_STATUS_MAP,
  FUND_ALLOCATION_STATUS_INDICATION_MAP,
  CLIENTS_SUBMODULE_TYPES,
  FinancialStatementsStatus,
  FinancialStatementType,
  FinancialStatementAuditStatus,
  FinancialStatementDocumentType,
  FinancialStatementAdHocAdjustmentType,
  ScenarioStatus,
  ScenarioOrigin,
  EPSILON,
  DD_TEMPLATE_WITH_NO_LANGUAGE_TOGGLE_SUPPORT,
  PvxImpactSummaryCardType,
  RATCHET_PRICING_METHODOLOGIES,
  USD_CURRENCY_CODE,
  SUPPORTS_CLAYRIO_ANALYSIS,
  ERROR_TYPE_FILTER_OPTIONS,
  ERROR_TYPE_FILTER_VALUES,
  DATA_FETCH_ERROR_TYPE_MAP,
  RERUN_FAILED_ERROR_TYPE_MAP,
  InvestorSigningAuthority,
  QueryParamsKeyEnum,
  MarketCeilingSpendType,
  TEXT_NA,
};
